// InstagramFeed.jsx
import { useEffect, useState } from 'react';
import './InstagramFeed.css';

const DEFAULT_JSON_URL =
    process.env.REACT_APP_INSTAGRAM_JSON_URL ||
    'https://storage.googleapis.com/zoomgroom-p01-public/instagram/latest.json';

export default function InstagramFeed({ jsonUrl = DEFAULT_JSON_URL, startingCount = 50 }) {
    const [items, setItems] = useState([]);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let dead = false;

        // 1) show any locally cached data immediately (fast paint)
        try {
            const cachedText = localStorage.getItem('ig_latest_json');
            if (cachedText) {
                const cached = JSON.parse(cachedText);
                const arr = Array.isArray(cached) ? cached : cached.items || [];
                if (!dead) {
                    setItems(arr.slice(0, startingCount));
                    setLoading(false);
                }
            }
        } catch {}

        // 2) fetch fresh JSON from GCS (or your redirect URL)
        fetch(jsonUrl, { cache: 'no-cache' })
            .then(async (r) => {
                const text = await r.text();
                let data;
                try {
                    data = JSON.parse(text);
                } catch {
                    throw new Error(`Non-JSON response (status ${r.status})`);
                }

                const arr = Array.isArray(data) ? data : data.items || [];
                if (!dead) {
                    setItems(arr.slice(0, startingCount));
                    setErr(null);
                }

                // store latest for instant loads next visit
                try {
                    localStorage.setItem('ig_latest_json', JSON.stringify(arr));
                } catch {}
            })
            .catch((e) => {
                if (!dead) setErr(e.message || 'Failed to load Instagram feed');
            })
            .finally(() => {
                if (!dead) setLoading(false);
            });

        return () => { dead = true; };
    }, [jsonUrl, startingCount]);

    if (loading) return <p style={{ textAlign: 'center', opacity: 0.7 }}>Loading Instagram…</p>;
    if (err) return <p style={{ textAlign: 'center', color: 'crimson' }}>Error: {err}</p>;

    return (
        <div className="ig-feed-grid">
            {items.map((it) => {
                const key = it.id;
                const href = it.permalink;
                const isVideo = it.media_type === 'VIDEO';
                const src = isVideo ? (it.thumbnail_url || it.media_url) : it.media_url;

                return (
                    <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ig-card"
                        aria-label={`Open Instagram post by ${it.username || 'Zoom Groom'}`}
                    >
                        <div className="ig-thumb-wrap">
                            <img src={src} alt={it.caption?.slice(0, 120) || 'Instagram post'} loading="lazy" />
                            {isVideo && <span className="ig-badge">Video</span>}
                        </div>
                        <div className="ig-meta">
                            {it.profile_picture_url && (
                                <img className="ig-avatar" src={it.profile_picture_url} alt={it.username} />
                            )}
                            <div className="ig-text">
                                <div className="ig-username">@{it.username}</div>
                                <div className="ig-time">{new Date(it.timestamp).toLocaleDateString()}</div>
                            </div>
                        </div>
                    </a>
                );
            })}
        </div>
    );
}
